import type { ApiResult } from "./contracts";

/**
 * Клиент настроек уведомлений (E10, «Настройки»). Доступен ТОЛЬКО администратору — сервер
 * проверяет право `settings:manage` на каждом маршруте (строка 4.3 «Настройки, справочники…»,
 * ТЗ 4.2.4 «правила уведомлений настраиваются администратором»). Путь относительный, сессия —
 * cookie HttpOnly (Р-5).
 *
 * Формы согласованы с сервером по значениям (`OrgSettingsView` из apps/api/src/settings/store.ts).
 * Все пороги — целые числа КАЛЕНДАРНЫХ дней (Р-32).
 */

export interface NotificationSettings {
  /** Порог эскалации: просрочка строго больше — сигнал директору и юристу (Прил. В: «более 3»). */
  readonly escalationDays: number;
  /** Окно «скоро срок» для денежных обязательств, дней (Прил. В: «за 3 дня»). */
  readonly notifyPayment: number;
  /** Окно «скоро срок» для поставки/приёмки, дней. */
  readonly notifyDelivery: number;
  /** Окно «скоро срок» для прочих типов, дней. */
  readonly notifyOther: number;
  /**
   * Приглушать эскалации и претензионные сигналы по аффилированным контрагентам (ТЗ 3.1).
   * По умолчанию ВЫКЛЮЧЕНО: по ответу юриста на В18 сигналы идут как для всех (Р-35).
   */
  readonly affiliatedMute: boolean;
}

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

interface ErrorBody {
  error?: string;
  message?: string;
}

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as ErrorBody | null;
  const message = body?.message;
  if (res.status === 403) {
    return {
      ok: false,
      reason: "forbidden",
      message: message ?? "Настройки уведомлений меняет только администратор.",
    };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

export async function fetchNotificationSettings(): Promise<ApiResult<NotificationSettings>> {
  let res: Response;
  try {
    res = await fetch("/api/settings/notifications", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { settings?: NotificationSettings } | null;
  if (!body?.settings) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: body.settings };
}

/** PUT: форма присылает все параметры целиком — частичного сохранения сервер не принимает. */
export async function saveNotificationSettings(
  input: NotificationSettings,
): Promise<ApiResult<NotificationSettings>> {
  let res: Response;
  try {
    res = await fetch("/api/settings/notifications", {
      method: "PUT",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { settings?: NotificationSettings } | null;
  if (!body?.settings) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: body.settings };
}

/** Целое число дней из поля ввода: пусто/мусор — `null` (форма покажет пояснение). */
export function parseDays(input: string): number | null {
  const s = input.trim();
  if (!/^\d{1,3}$/.test(s)) return null;
  const value = Number(s);
  return value >= 0 && value <= 365 ? value : null;
}
